'use client';

import React, { useEffect, useState, useContext, useMemo, useRef } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { supabase } from '../../supabaseClient';
import { useParams } from 'react-router-dom';
import * as Chart from 'chart.js';

// Register Chart.js components
Chart.Chart.register(
  Chart.CategoryScale,
  Chart.LinearScale,
  Chart.PointElement,
  Chart.LineElement,
  Chart.BarElement,
  Chart.Title,
  Chart.Tooltip,
  Chart.Legend,
  Chart.Filler,
  Chart.ScatterController,
  Chart.LineController,
  Chart.BarController
);

// Custom date formatting function
const formatDate = (timestamp, formatStr = 'MMM d, HH:mm') => {
  const date = new Date(timestamp);
  if (formatStr === 'MMM d, HH:mm') {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + 
           ', ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  }
  if (formatStr === 'MMM d, HH:mm:ss') {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + 
           ', ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
  }
  if (formatStr === 'MMM d') {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  if (formatStr === 'MMM d, yyyy') {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  if (formatStr === 'MMM d, yyyy HH:mm:ss') {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + 
           ', ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
  }
  return date.toLocaleDateString();
};

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const StatCard = ({ title, value, unit, status, description, trend, infoTooltip }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  const statusColors = {
    good: 'bg-green-50 border-green-200 text-green-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    critical: 'bg-red-50 border-red-200 text-red-800',
    neutral: 'bg-gray-50 border-gray-200 text-gray-800'
  };

  return (
    <div className={`p-4 rounded-lg border-2 relative ${statusColors[status] || statusColors.neutral}`}>
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium opacity-75">{title}</p>
            {infoTooltip && (
              <div className="relative">
                <div
                  className="w-4 h-4 rounded-full bg-gray-400 text-white text-xs flex items-center justify-center cursor-help ml-2"
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                >
                  i
                </div>
                {showTooltip && (
                  <div className="absolute right-0 top-6 z-10 w-64 p-3 bg-gray-900 text-white text-sm rounded-lg shadow-lg">
                    <div className="absolute -top-1 right-2 w-2 h-2 bg-gray-900 rotate-45"></div>
                    {infoTooltip}
                  </div>
                )}
              </div>
            )}
          </div>
          <p className="text-2xl font-bold mt-1">
            {typeof value === 'number' ? value.toFixed(2) : value}
            {unit && <span className="text-lg font-normal ml-1">{unit}</span>}
          </p>
          {description && <p className="text-xs mt-1 opacity-75">{description}</p>}
          {trend && (
            <div className="flex items-center mt-2">
              <span className={`text-xs font-medium ${trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                {trend > 0 ? '↗ ' : trend < 0 ? '↘ ' : '→ '}
                {Math.abs(trend).toFixed(1)}%
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DistributionChart = ({ data, dataKey, title, unit, referenceRange }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const values = data.map(d => d[dataKey]).filter(v => v != null);
  
  const bins = useMemo(() => {
    if (values.length === 0) return [];
    
    const min = Math.min(...values);
    const max = Math.max(...values);
    const binCount = 15;
    const binWidth = (max - min) / binCount;
    
    return Array.from({ length: binCount }, (_, i) => {
      const binStart = min + i * binWidth;
      const binEnd = binStart + binWidth;
      const count = values.filter(v => v >= binStart && v < binEnd).length;
      return {
        label: `${binStart.toFixed(1)}-${binEnd.toFixed(1)}`,
        count,
        midpoint: (binStart + binEnd) / 2
      };
    });
  }, [values]);

  useEffect(() => {
    if (chartRef.current && values.length > 0) {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }

      const ctx = chartRef.current.getContext('2d');
      chartInstance.current = new Chart.Chart(ctx, {
        type: 'bar',
        data: {
          labels: bins.map(bin => bin.label),
          datasets: [{
            label: 'Frequency',
            data: bins.map(bin => bin.count),
            backgroundColor: '#059669', // Emerald-600 for better visibility
            borderColor: '#047857', // Emerald-700
            borderWidth: 1,
            borderRadius: 4,
            borderSkipped: false,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          onHover: (event, elements) => {
            event.native.target.style.cursor = elements.length > 0 && elements[0].datasetIndex === 0 ? 'pointer' : 'default';
          },
          scales: {
            x: {
              title: {
                display: true,
                text: `${title} (${unit})`,
                color: '#374151',
                font: { size: 12, weight: 'bold' }
              },
              grid: {
                color: '#f3f4f6'
              },
              ticks: {
                color: '#6b7280'
              }
            },
            y: {
              title: {
                display: true,
                text: 'Frequency',
                color: '#374151',
                font: { size: 12, weight: 'bold' }
              },
              beginAtZero: true,
              grid: {
                color: '#f3f4f6'
              },
              ticks: {
                color: '#6b7280'
              }
            }
          },
          plugins: {
            tooltip: {
              backgroundColor: '#1f2937',
              titleColor: '#f9fafb',
              bodyColor: '#f9fafb',
              borderColor: '#374151',
              borderWidth: 1,
              callbacks: {
                title: function(context) {
                  return `${title}: ${context[0].label} ${unit}`;
                },
                label: function(context) {
                  return `Count: ${context.parsed.y}`;
                }
              }
            },
            legend: {
              display: false
            }
          }
        }
      });
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [data, dataKey, title, unit, referenceRange, bins]);

  if (values.length === 0) {
    return <div className="bg-white p-6 rounded-lg border-2 border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title} Distribution</h3>
      <div className="text-center text-gray-500 py-8">No data available</div>
    </div>;
  }

  return (
    <div className="bg-white p-6 rounded-lg border-2 border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title} Distribution</h3>
      <div style={{ height: '400px' }}>
        <canvas ref={chartRef}></canvas>
      </div>
    </div>
  );
};

const CorrelationChart = ({ data, detectUnits, correlation, formatNumber }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const chartData = data
    .filter(d => d.Density != null && d.SurfaceTemp != null)
    .sort((a, b) => a.SurfaceTemp - b.SurfaceTemp)
    .map(d => ({
      x: d.SurfaceTemp,
      y: d.Density
    }));

  // Calculate linear regression line
  const regressionLine = useMemo(() => {
    if (chartData.length < 2) return null;
    
    const n = chartData.length;
    const sumX = chartData.reduce((sum, point) => sum + point.x, 0);
    const sumY = chartData.reduce((sum, point) => sum + point.y, 0);
    const sumXY = chartData.reduce((sum, point) => sum + point.x * point.y, 0);
    const sumX2 = chartData.reduce((sum, point) => sum + point.x * point.x, 0);
    
    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    
    if (isNaN(slope) || isNaN(intercept)) return null;
    
    const xMin = Math.min(...chartData.map(d => d.x));
    const xMax = Math.max(...chartData.map(d => d.x));
    
    return [
      { x: xMin, y: slope * xMin + intercept },
      { x: xMax, y: slope * xMax + intercept }
    ];
  }, [chartData]);

  useEffect(() => {
    if (chartRef.current) {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }

      const datasets = [{
        label: 'Density vs Temperature',
        data: chartData,
        backgroundColor: '#2563eb', // Blue-600
        borderColor: '#1d4ed8', // Blue-700
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBorderWidth: 2,
        pointBorderColor: '#ffffff',
        type: 'scatter'
      }];

      // Add regression line if calculated successfully
      if (regressionLine) {
        datasets.push({
          label: 'Trend Line',
          data: regressionLine,
          borderColor: '#dc2626', // Red-600
          backgroundColor: 'transparent',
          borderWidth: 3,
          pointRadius: 0,
          pointHoverRadius: 0,
          type: 'line',
          tension: 0,
          borderDash: [8, 4]
        });
      }

      const ctx = chartRef.current.getContext('2d');
      chartInstance.current = new Chart.Chart(ctx, {
        type: 'scatter',
        data: {
          datasets: datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              type: 'linear',
              title: {
                display: true,
                text: `Surface Temperature (${detectUnits.temperature})`,
                color: '#374151',
                font: { size: 12, weight: 'bold' }
              },
              grid: {
                color: '#f3f4f6'
              },
              ticks: {
                color: '#6b7280',
                callback: function(value) {
                  return formatNumber(value, detectUnits.temperature);
                }
              }
            },
            y: {
              title: {
                display: true,
                text: `Density (${detectUnits.density})`,
                color: '#374151',
                font: { size: 12, weight: 'bold' }
              },
              grid: {
                color: '#f3f4f6'
              },
              ticks: {
                color: '#6b7280',
                callback: function(value) {
                  return formatNumber(value, detectUnits.density);
                }
              }
            }
          },
          plugins: {
            tooltip: {
              backgroundColor: '#1f2937',
              titleColor: '#f9fafb',
              bodyColor: '#f9fafb',
              borderColor: '#374151',
              borderWidth: 1,
              callbacks: {
                label: function(context) {
                  if (context.datasetIndex === 0) {
                    return [
                      `Temperature: ${formatNumber(context.parsed.x, detectUnits.temperature)} ${detectUnits.temperature}`,
                      `Density: ${formatNumber(context.parsed.y, detectUnits.density)} ${detectUnits.density}`
                    ];
                  }
                  return null;
                }
              },
              filter: function(tooltipItem) {
                return tooltipItem.datasetIndex === 0;
              }
            },
            legend: {
              display: true,
              position: 'bottom',
              onClick: function(event, legendItem, legend) {
                return;
              },
              labels: {
                generateLabels: function(chart) {
                  const datasets = chart.data.datasets;
                  const labels = [];
                  
                  datasets.forEach((dataset, index) => {
                    if (index > 0) {
                      labels.push({
                        text: dataset.label,
                        strokeStyle: dataset.borderColor,
                        lineWidth: dataset.borderWidth,
                        fillStyle: dataset.borderColor,
                        pointStyle: 'line'
                      });
                    }
                  });
                  
                  return labels;
                }
              }
            }
          }
        }
      });
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [data, detectUnits, formatNumber, regressionLine]);

  return (
    <div className="bg-white p-6 rounded-lg border-2 border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Density vs Surface Temperature Correlation</h3>
      <div style={{ height: '400px' }}>
        <canvas ref={chartRef}></canvas>
      </div>
      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-700">
          <strong>Correlation Coefficient:</strong> {correlation.toFixed(3)}
          <span className="ml-4">
            <strong>Interpretation:</strong> {
              Math.abs(correlation) > 0.7 ? 'Strong correlation' :
              Math.abs(correlation) > 0.3 ? 'Moderate correlation' :
              'Weak correlation'
            } between density and temperature
          </span>
        </p>
      </div>
    </div>
  );
};

const TrendsChart = ({ data, selectedMetric, currentMetricProps, statistics, formatNumber }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const chartData = useMemo(() => 
    data.filter(d => d[currentMetricProps.dataKey] != null).map(d => ({
      x: new Date(d.LocalTimeOfMeasurement).getTime(),
      y: d[currentMetricProps.dataKey]
    })), [data, currentMetricProps.dataKey]
  );

  useEffect(() => {
    if (chartRef.current) {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }

      const ctx = chartRef.current.getContext('2d');
      
      const datasets = [{
        label: selectedMetric.charAt(0).toUpperCase() + selectedMetric.slice(1),
        data: chartData,
        borderColor: '#059669', // Emerald-600
        backgroundColor: '#059669',
        borderWidth: 3,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: '#10b981', // Emerald-500
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        tension: 0.1,
        type: 'line'
      }];

      if (currentMetricProps.range && chartData.length > 0) {
        const xMin = Math.min(...chartData.map(d => d.x));
        const xMax = Math.max(...chartData.map(d => d.x));
        
        // Spec Max line
        datasets.push({
          label: 'Spec Max',
          data: [
            { x: xMin, y: currentMetricProps.range.max },
            { x: xMax, y: currentMetricProps.range.max }
          ],
          borderColor: '#7c3aed', // Violet-600
          backgroundColor: 'transparent',
          borderWidth: 2,
          borderDash: [10, 5],
          pointRadius: 0,
          pointHoverRadius: 0,
          type: 'line',
          tension: 0
        });

        datasets.push({
          label: 'Spec Min',
          data: [
            { x: xMin, y: currentMetricProps.range.min },
            { x: xMax, y: currentMetricProps.range.min }
          ],
          borderColor: '#7c3aed', // Violet-600
          backgroundColor: 'transparent',
          borderWidth: 2,
          borderDash: [10, 5],
          pointRadius: 0,
          pointHoverRadius: 0,
          type: 'line',
          tension: 0
        });
      }

      if (statistics[selectedMetric] && chartData.length > 0) {
        const xMin = Math.min(...chartData.map(d => d.x));
        const xMax = Math.max(...chartData.map(d => d.x));
        
        datasets.push({
          label: 'Mean',
          data: [
            { x: xMin, y: statistics[selectedMetric].mean },
            { x: xMax, y: statistics[selectedMetric].mean }
          ],
          borderColor: '#dc2626', // Red-600
          backgroundColor: 'transparent',
          borderWidth: 2,
          borderDash: [5, 5],
          pointRadius: 0,
          pointHoverRadius: 0,
          type: 'line',
          tension: 0
        });
      }

      chartInstance.current = new Chart.Chart(ctx, {
        type: 'line',
        data: {
          datasets: datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              type: 'linear',
              title: {
                display: true,
                text: 'Date & Time',
                color: '#374151',
                font: { size: 12, weight: 'bold' }
              },
              grid: {
                color: '#f3f4f6'
              },
              ticks: {
                color: '#6b7280',
                callback: function(value) {
                  return formatDate(value, 'MMM d, HH:mm:ss');
                },
                maxTicksLimit: 8
              }
            },
            y: {
              title: {
                display: true,
                text: `${selectedMetric.charAt(0).toUpperCase() + selectedMetric.slice(1)} (${currentMetricProps.unit})`,
                color: '#374151',
                font: { size: 12, weight: 'bold' }
              },
              grid: {
                color: '#f3f4f6'
              },
              ticks: {
                color: '#6b7280',
                callback: function(value) {
                  return formatNumber(value, currentMetricProps.unit);
                }
              }
            }
          },
          plugins: {
            tooltip: {
              backgroundColor: '#1f2937',
              titleColor: '#f9fafb',
              bodyColor: '#f9fafb',
              borderColor: '#374151',
              borderWidth: 1,
              callbacks: {
                title: function(context) {
                  return formatDate(context[0].parsed.x, 'MMM d, yyyy HH:mm:ss');
                },
                label: function(context) {
                  if (context.datasetIndex === 0) {
                    return `${selectedMetric.charAt(0).toUpperCase() + selectedMetric.slice(1)}: ${formatNumber(context.parsed.y, currentMetricProps.unit)} ${currentMetricProps.unit}`;
                  }
                  return null;
                }
              },
              filter: function(tooltipItem) {
                return tooltipItem.datasetIndex === 0;
              }
            },
            legend: {
              display: true,
              position: 'bottom',
              onClick: function(event, legendItem, legend) {
                return;
              },
              labels: {
                generateLabels: function(chart) {
                  const datasets = chart.data.datasets;
                  const labels = [];
                  
                  datasets.forEach((dataset, index) => {
                    if (index > 0) {
                      labels.push({
                        text: dataset.label,
                        strokeStyle: dataset.borderColor,
                        lineWidth: dataset.borderWidth,
                        lineDash: dataset.borderDash || [],
                        fillStyle: dataset.borderColor,
                        pointStyle: 'line'
                      });
                    }
                  });
                  
                  return labels;
                }
              }
            }
          }
        }
      });
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [data, selectedMetric, currentMetricProps, formatNumber, statistics]);

  return (
    <div className="bg-white p-6 rounded-lg border-2 border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        {selectedMetric.charAt(0).toUpperCase() + selectedMetric.slice(1)} Time Trends
      </h3>
      <div style={{ height: '400px' }}>
        <canvas ref={chartRef}></canvas>
      </div>
    </div>
  );
};

const ControlChart = ({ data, dataKey, title, unit, referenceRange, onDateRangeChange = null }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const values = data.map(d => d[dataKey]).filter(v => v != null);
  
  const mean = values.length > 0 ? values.reduce((sum, v) => sum + v, 0) / values.length : 0;
  const variance = values.length > 1 ? values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / (values.length - 1) : 0;
  const stdDev = Math.sqrt(variance);
  
  const ucl = mean + 3 * stdDev;
  const lcl = mean - 3 * stdDev;
  const uwl = mean + 2 * stdDev;
  const lwl = mean - 2 * stdDev;

  const chartData = useMemo(() => 
    data.filter(d => d[dataKey] != null).map((d) => ({
      x: new Date(d.LocalTimeOfMeasurement).getTime(),
      y: d[dataKey]
    })), [data, dataKey]
  );

  // Format Y-axis values based on unit and range
  const formatYAxisValue = (value) => {
    if (unit === '%') {
      return `${value.toFixed(1)}%`;
    } else if (unit === 'kg/m³') {
      return value.toFixed(0);
    } else if (unit === '°F' || unit === '°C') {
      return `${value.toFixed(0)}${unit}`;
    } else {
      return value.toFixed(1);
    }
  };

  useEffect(() => {
    if (chartRef.current && values.length > 0 && chartData.length > 0) {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }

      const ctx = chartRef.current.getContext('2d');
      
      // Create datasets array starting with the main scatter data
      const datasets = [{
        label: title,
        data: chartData,
        backgroundColor: chartData.map(point => {
          const value = point.y;
          // Use specification range if available, otherwise use control limits
          if (referenceRange) {
            return (value >= referenceRange.min && value <= referenceRange.max) ? '#10b981' : '#dc2626'; // Green for within spec, red for outside
          } else {
            if (value > ucl || value < lcl) return '#dc2626'; // Red
            if (value > uwl || value < lwl) return '#f59e0b'; // Yellow
            return '#10b981'; // Green
          }
        }),
        borderColor: chartData.map(point => {
          const value = point.y;
          if (referenceRange) {
            return (value >= referenceRange.min && value <= referenceRange.max) ? '#059669' : '#b91c1c'; // Darker shades for borders
          } else {
            if (value > ucl || value < lcl) return '#b91c1c';
            if (value > uwl || value < lwl) return '#d97706';
            return '#059669';
          }
        }),
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBorderWidth: 2,
        pointBorderColor: '#ffffff',
        type: 'scatter'
      }];

      // Add reference lines if specification range exists
      if (referenceRange && chartData.length > 0) {
        const xMin = Math.min(...chartData.map(d => d.x));
        const xMax = Math.max(...chartData.map(d => d.x));
        
        // Spec Max line
        datasets.push({
          label: 'Spec Max',
          data: [
            { x: xMin, y: referenceRange.max },
            { x: xMax, y: referenceRange.max }
          ],
          borderColor: '#7c3aed', // Violet-600
          backgroundColor: 'transparent',
          borderWidth: 3,
          borderDash: [10, 5],
          pointRadius: 0,
          pointHoverRadius: 0,
          type: 'line',
          tension: 0
        });

        // Spec Min line
        datasets.push({
          label: 'Spec Min',
          data: [
            { x: xMin, y: referenceRange.min },
            { x: xMax, y: referenceRange.min }
          ],
          borderColor: '#7c3aed', // Violet-600
          backgroundColor: 'transparent',
          borderWidth: 3,
          borderDash: [10, 5],
          pointRadius: 0,
          pointHoverRadius: 0,
          type: 'line',
          tension: 0
        });
      }

      // Add mean line (now red and dashed as requested)
      if (chartData.length > 0) {
        const xMin = Math.min(...chartData.map(d => d.x));
        const xMax = Math.max(...chartData.map(d => d.x));
        
        datasets.push({
          label: 'Mean',
          data: [
            { x: xMin, y: mean },
            { x: xMax, y: mean }
          ],
          borderColor: '#dc2626', // Red-600
          backgroundColor: 'transparent',
          borderWidth: 3,
          borderDash: [8, 4], // Dashed line
          pointRadius: 0,
          pointHoverRadius: 0,
          type: 'line',
          tension: 0
        });
      }

      chartInstance.current = new Chart.Chart(ctx, {
        type: 'scatter',
        data: {
          datasets: datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          onClick: (event, elements) => {
            if (elements.length > 0 && elements[0].datasetIndex === 0 && onDateRangeChange) {
              const clickedElement = elements[0];
              const clickedData = chartData[clickedElement.index];
              
              if (clickedData && clickedData.x) {
                const clickedDate = new Date(clickedData.x);
                const year = clickedDate.getFullYear();
                const month = clickedDate.getMonth();
                const day = clickedDate.getDate();
                
                const startOfDay = new Date(year, month, day);
                const endOfDay = new Date(year, month, day + 1);
                
                const formatLocalDate = (date) => {
                  const localYear = date.getFullYear();
                  const localMonth = String(date.getMonth() + 1).padStart(2, '0');
                  const localDay = String(date.getDate()).padStart(2, '0');
                  return `${localYear}-${localMonth}-${localDay}`;
                };
                
                onDateRangeChange({
                  dateRange: 'custom',
                  startDate: formatLocalDate(startOfDay),
                  endDate: formatLocalDate(endOfDay)
                });
              }
            }
          },
          scales: {
            x: {
              type: 'linear',
              title: {
                display: true,
                text: 'Date & Time',
                color: '#374151',
                font: { size: 12, weight: 'bold' }
              },
              grid: {
                color: '#f3f4f6'
              },
              ticks: {
                color: '#6b7280',
                callback: function(value) {
                  return formatDate(value, 'MMM d, HH:mm:ss');
                },
                maxTicksLimit: 8
              }
            },
            y: {
              title: {
                display: true,
                text: `${title} (${unit})`,
                color: '#374151',
                font: { size: 12, weight: 'bold' }
              },
              grid: {
                color: '#f3f4f6'
              },
              ticks: {
                color: '#6b7280',
                callback: function(value) {
                  return formatYAxisValue(value);
                }
              }
            }
          },
          plugins: {
            tooltip: {
              backgroundColor: '#1f2937',
              titleColor: '#f9fafb',
              bodyColor: '#f9fafb',
              borderColor: '#374151',
              borderWidth: 1,
              callbacks: {
                title: function(context) {
                  return formatDate(context[0].parsed.x, 'MMM d, yyyy HH:mm:ss');
                },
                label: function(context) {
                  if (context.datasetIndex === 0) {
                    return `${title}: ${formatYAxisValue(context.parsed.y)}`;
                  }
                  return null;
                }
              },
              filter: function(tooltipItem) {
                return tooltipItem.datasetIndex === 0;
              }
            },
            legend: {
              display: true,
              position: 'bottom',
              onClick: function(event, legendItem, legend) {
                return;
              },
              labels: {
                generateLabels: function(chart) {
                  const datasets = chart.data.datasets;
                  const labels = [];
                  
                  datasets.forEach((dataset, index) => {
                    if (index > 0) {
                      labels.push({
                        text: dataset.label,
                        strokeStyle: dataset.borderColor,
                        lineWidth: dataset.borderWidth,
                        lineDash: dataset.borderDash || [],
                        fillStyle: dataset.borderColor,
                        pointStyle: 'line'
                      });
                    }
                  });
                  
                  return labels;
                }
              }
            }
          }
        }
      });
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [data, dataKey, title, unit, referenceRange, mean, ucl, lcl, uwl, lwl, onDateRangeChange]);

  if (values.length === 0) {
    return <div className="bg-white p-6 rounded-lg border-2 border-gray-200 col-span-full">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title} Control Chart</h3>
      <div className="text-center text-gray-500 py-8">No data available</div>
    </div>;
  }

  return (
    <div className="bg-white p-6 rounded-lg border-2 border-gray-200 col-span-full">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{title} Control Chart</h3>
        <p className="text-xs text-gray-500 italic mt-1">💡 Click on any point to filter to that day</p>
      </div>
      <div className="mb-4 text-sm text-gray-600 grid grid-cols-2 lg:grid-cols-3 gap-4">
        <div>Mean: <span className="font-medium text-red-600">{formatYAxisValue(mean)}</span></div>
        <div>UCL (+3σ): <span className="font-medium text-red-600">{formatYAxisValue(ucl)}</span></div>
        <div>LCL (-3σ): <span className="font-medium text-red-600">{formatYAxisValue(lcl)}</span></div>
        <div>UWL (+2σ): <span className="font-medium text-yellow-600">{formatYAxisValue(uwl)}</span></div>
        <div>LWL (-2σ): <span className="font-medium text-yellow-600">{formatYAxisValue(lwl)}</span></div>
        {referenceRange && (
          <div>Spec: <span className="font-medium text-purple-600">{formatYAxisValue(referenceRange.min)} - {formatYAxisValue(referenceRange.max)}</span></div>
        )}
      </div>
      <div style={{ height: '500px' }}>
        <canvas ref={chartRef}></canvas>
      </div>
    </div>
  );
};

export default function ProjectAnalytics() {
  const { user } = useContext(AuthContext);
  const { projectId } = useParams();
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState('compaction');
  const [analysisType, setAnalysisType] = useState('control');
  const [dateRange, setDateRange] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [dateRangeNotification, setDateRangeNotification] = useState('');
  const [isFiltered, setIsFiltered] = useState(false);

  // Configurable reference ranges
  const [densityRange, setDensityRange] = useState({ min: 142, max: 148 });
  const [compactionRange, setCompactionRange] = useState({ min: 92, max: 100 });
  const [voidsRange, setVoidsRange] = useState({ min: 3, max: 7 });
  const [tempRange, setTempRange] = useState({ min: 250, max: 350 });
  
  // Pending ranges for input validation
  const [pendingDensityRange, setPendingDensityRange] = useState({ min: '142', max: '148' });
  const [pendingCompactionRange, setPendingCompactionRange] = useState({ min: '92', max: '100' });
  const [pendingVoidsRange, setPendingVoidsRange] = useState({ min: '3', max: '7' });
  const [pendingTempRange, setPendingTempRange] = useState({ min: '250', max: '350' });
  
  const [showRangeConfig, setShowRangeConfig] = useState(false);

  // Filter measurements based on date range
  const filteredMeasurements = useMemo(() => {
    if (!measurements.length) return [];

    let filtered = [...measurements];
    const now = new Date();

    switch (dateRange) {
      case 'week':
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        filtered = filtered.filter(m => new Date(m.LocalTimeOfMeasurement) >= weekAgo);
        break;
      case 'month':
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        filtered = filtered.filter(m => new Date(m.LocalTimeOfMeasurement) >= monthAgo);
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          const [startYear, startMonth, startDay] = customStartDate.split('-').map(Number);
          const [endYear, endMonth, endDay] = customEndDate.split('-').map(Number);
          
          const start = new Date(startYear, startMonth - 1, startDay);
          const end = new Date(endYear, endMonth - 1, endDay);
          
          filtered = filtered.filter(m => {
            const date = new Date(m.LocalTimeOfMeasurement);
            return date >= start && date < end;
          });
        }
        break;
      default:
        break;
    }

    return filtered.sort((a, b) => new Date(a.LocalTimeOfMeasurement) - new Date(b.LocalTimeOfMeasurement));
  }, [measurements, dateRange, customStartDate, customEndDate]);

  // Auto-detect units based on data ranges
  const detectUnits = useMemo(() => {
    if (!filteredMeasurements.length) return { density: 'pcf', temperature: '°F' };
    
    const densityValues = filteredMeasurements.map(m => m.Density).filter(v => v != null);
    const tempValues = filteredMeasurements.map(m => m.SurfaceTemp).filter(v => v != null);
    
    const avgDensity = densityValues.length ? densityValues.reduce((sum, v) => sum + v, 0) / densityValues.length : 0;
    const densityUnit = avgDensity > 1000 ? 'kg/m³' : 'pcf';
    
    const avgTemp = tempValues.length ? tempValues.reduce((sum, v) => sum + v, 0) / tempValues.length : 0;
    const tempUnit = avgTemp > 100 ? '°F' : '°C';
    
    return { density: densityUnit, temperature: tempUnit };
  }, [filteredMeasurements]);

  // Update default ranges when units change
  useEffect(() => {
    if (detectUnits.density === 'kg/m³') {
      setDensityRange({ min: 2275, max: 2370 });
      setPendingDensityRange({ min: '2275', max: '2370' });
    } else {
      setDensityRange({ min: 142, max: 148 });
      setPendingDensityRange({ min: '142', max: '148' });
    }
    
    if (detectUnits.temperature === '°C') {
      setTempRange({ min: 120, max: 180 });
      setPendingTempRange({ min: '120', max: '180' });
    } else {
      setTempRange({ min: 250, max: 350 });
      setPendingTempRange({ min: '250', max: '350' });
    }
  }, [detectUnits]);

  // Format numbers for better readability
  const formatNumber = (value, unit) => {
    if (value == null || isNaN(value)) return 'N/A';
    
    if (unit === 'kg/m³' || value > 1000) {
      return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
    } else if (unit === '°F' || unit === '°C') {
      return value.toFixed(0);
    } else {
      return value.toFixed(1);
    }
  };

  // Handle range updates with validation
  const updateRange = (setter, pendingSetter, pendingRange, rangeName) => {
    const min = parseFloat(pendingRange.min);
    const max = parseFloat(pendingRange.max);
    
    if (isNaN(min) || isNaN(max)) {
      alert(`Please enter valid numbers for ${rangeName} range.`);
      return;
    }
    
    if (min >= max) {
      alert(`Minimum ${rangeName} must be less than maximum.`);
      return;
    }
    
    setter({ min, max });
  };

  // Reset date filter
  const resetDateFilter = () => {
    setDateRange('all');
    setCustomStartDate('');
    setCustomEndDate('');
    setIsFiltered(false);
    setDateRangeNotification('');
  };

  // Helper function to get metric properties
  const getMetricProps = (metric) => {
    switch (metric) {
      case 'density':
        return {
          dataKey: 'Density',
          unit: detectUnits.density,
          range: densityRange
        };
      case 'compaction':
        return {
          dataKey: 'Compaction',
          unit: '%',
          range: compactionRange
        };
      case 'voids':
        return {
          dataKey: 'Voids',
          unit: '%',
          range: voidsRange
        };
      case 'temperature':
        return {
          dataKey: 'SurfaceTemp',
          unit: detectUnits.temperature,
          range: tempRange
        };
      default:
        return {
          dataKey: 'Density',
          unit: detectUnits.density,
          range: densityRange
        };
    }
  };

  useEffect(() => {
    const fetchMeasurements = async () => {
      if (!user || !projectId) return;
      setLoading(true);
      try {
        const { data: mappingData, error: mappingError } = await supabase
          .from('project_mapping')
          .select('measurement:measurements(*)')
          .eq('project_id', projectId);

        if (mappingError) {
          console.error('Error fetching project mappings:', mappingError);
        } else if (mappingData) {
          const ms = mappingData.map((item) => item.measurement);
          setMeasurements(ms);
        }
      } catch (err) {
        console.error('Unexpected error fetching measurements:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMeasurements();
  }, [user, projectId]);

  // Calculate comprehensive statistics
  const statistics = useMemo(() => {
    const calculateStats = (values, range) => {
      const validValues = values.filter(v => v != null && !isNaN(v));
      if (validValues.length === 0) return null;

      const sorted = [...validValues].sort((a, b) => a - b);
      const mean = validValues.reduce((sum, v) => sum + v, 0) / validValues.length;
      const variance = validValues.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / (validValues.length - 1);
      const stdDev = Math.sqrt(variance);
      const median = sorted[Math.floor(sorted.length / 2)];
      
      const withinSpec = range ? validValues.filter(v => v >= range.min && v <= range.max).length : validValues.length;
      const passRate = (withinSpec / validValues.length) * 100;
      
      const cpk = range ? Math.min(
        (range.max - mean) / (3 * stdDev),
        (mean - range.min) / (3 * stdDev)
      ) : 0;

      return {
        count: validValues.length,
        mean,
        median,
        stdDev,
        min: Math.min(...validValues),
        max: Math.max(...validValues),
        passRate,
        cpk,
        range: Math.max(...validValues) - Math.min(...validValues)
      };
    };

    const densityValues = filteredMeasurements.map(m => m.Density);
    const compactionValues = filteredMeasurements.map(m => m.Compaction);
    const voidsValues = filteredMeasurements.map(m => m.Voids);
    const tempValues = filteredMeasurements.map(m => m.SurfaceTemp);

    return {
      density: calculateStats(densityValues, densityRange),
      compaction: calculateStats(compactionValues, compactionRange),
      voids: calculateStats(voidsValues, voidsRange),
      temperature: calculateStats(tempValues, tempRange)
    };
  }, [filteredMeasurements, densityRange, compactionRange, voidsRange, tempRange]);

  const correlation = useMemo(() => {
    const densityValues = filteredMeasurements.map(m => m.Density).filter(v => v != null);
    const tempValues = filteredMeasurements.map(m => m.SurfaceTemp).filter(v => v != null);
    
    if (densityValues.length !== tempValues.length || densityValues.length < 2) return 0;
    
    const n = densityValues.length;
    const sumX = densityValues.reduce((sum, v) => sum + v, 0);
    const sumY = tempValues.reduce((sum, v) => sum + v, 0);
    const sumXY = densityValues.reduce((sum, v, i) => sum + v * tempValues[i], 0);
    const sumX2 = densityValues.reduce((sum, v) => sum + v * v, 0);
    const sumY2 = tempValues.reduce((sum, v) => sum + v * v, 0);
    
    const numerator = n * sumXY - sumX * sumY;
    const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));
    
    return denominator === 0 ? 0 : numerator / denominator;
  }, [filteredMeasurements]);

  const getStatusFromCpk = (cpk) => {
    if (cpk >= 1.33) return 'good';
    if (cpk >= 1.0) return 'warning';
    return 'critical';
  };

  const getStatusFromPassRate = (rate) => {
    if (rate >= 95) return 'good';
    if (rate >= 85) return 'warning';
    return 'critical';
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-24 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
          <div className="h-96 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  const currentMetricProps = getMetricProps(selectedMetric);

  return (
    <div className="space-y-6">
      {/* Date Range Notification */}
      {dateRangeNotification && (
        <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg animate-pulse">
          <div className="flex items-center">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
            {dateRangeNotification}
          </div>
        </div>
      )}
      
      {/* Header and Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Quality Control Analytics</h2>
          <p className="text-sm text-gray-600">Statistical analysis and process control monitoring</p>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Configure Ranges Button */}
          <button
            onClick={() => setShowRangeConfig(!showRangeConfig)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
          >
            <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Configure Limits
          </button>
          
          {/* Date Range Selector */}
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="rounded-md border-gray-300 text-sm focus:border-yellow-500 focus:ring-yellow-500"
          >
            <option value="all">All Time</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="custom">Custom Range</option>
          </select>
          
          {dateRange === 'custom' && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="rounded-md border-gray-300 text-sm focus:border-yellow-500 focus:ring-yellow-500"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="rounded-md border-gray-300 text-sm focus:border-yellow-500 focus:ring-yellow-500"
              />
            </div>
          )}
        </div>
      </div>

      {/* Range Configuration Panel */}
      {showRangeConfig && (
        <div className="bg-white rounded-lg border-2 border-gray-200">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Configure Specification Limits</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Density Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Density Range ({detectUnits.density})
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={pendingDensityRange.min}
                    onChange={(e) => setPendingDensityRange(prev => ({ ...prev, min: e.target.value }))}
                    className="w-20 rounded-md border-gray-300 text-sm focus:border-yellow-500 focus:ring-yellow-500"
                    placeholder="Min"
                  />
                  <span className="text-gray-500">to</span>
                  <input
                    type="number"
                    value={pendingDensityRange.max}
                    onChange={(e) => setPendingDensityRange(prev => ({ ...prev, max: e.target.value }))}
                    className="w-20 rounded-md border-gray-300 text-sm focus:border-yellow-500 focus:ring-yellow-500"
                    placeholder="Max"
                  />
                  <button
                    onClick={() => updateRange(setDensityRange, setPendingDensityRange, pendingDensityRange, 'Density')}
                    className="px-2 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700"
                  >
                    Update
                  </button>
                </div>
              </div>

              {/* Compaction Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Compaction Range (%)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={pendingCompactionRange.min}
                    onChange={(e) => setPendingCompactionRange(prev => ({ ...prev, min: e.target.value }))}
                    className="w-20 rounded-md border-gray-300 text-sm focus:border-yellow-500 focus:ring-yellow-500"
                    placeholder="Min"
                  />
                  <span className="text-gray-500">to</span>
                  <input
                    type="number"
                    value={pendingCompactionRange.max}
                    onChange={(e) => setPendingCompactionRange(prev => ({ ...prev, max: e.target.value }))}
                    className="w-20 rounded-md border-gray-300 text-sm focus:border-yellow-500 focus:ring-yellow-500"
                    placeholder="Max"
                  />
                  <button
                    onClick={() => updateRange(setCompactionRange, setPendingCompactionRange, pendingCompactionRange, 'Compaction')}
                    className="px-2 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700"
                  >
                    Update
                  </button>
                </div>
              </div>

              {/* Voids Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Voids Range (%)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={pendingVoidsRange.min}
                    onChange={(e) => setPendingVoidsRange(prev => ({ ...prev, min: e.target.value }))}
                    className="w-20 rounded-md border-gray-300 text-sm focus:border-yellow-500 focus:ring-yellow-500"
                    placeholder="Min"
                  />
                  <span className="text-gray-500">to</span>
                  <input
                    type="number"
                    value={pendingVoidsRange.max}
                    onChange={(e) => setPendingVoidsRange(prev => ({ ...prev, max: e.target.value }))}
                    className="w-20 rounded-md border-gray-300 text-sm focus:border-yellow-500 focus:ring-yellow-500"
                    placeholder="Max"
                  />
                  <button
                    onClick={() => updateRange(setVoidsRange, setPendingVoidsRange, pendingVoidsRange, 'Voids')}
                    className="px-2 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700"
                  >
                    Update
                  </button>
                </div>
              </div>

              {/* Temperature Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Temperature Range ({detectUnits.temperature})
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={pendingTempRange.min}
                    onChange={(e) => setPendingTempRange(prev => ({ ...prev, min: e.target.value }))}
                    className="w-20 rounded-md border-gray-300 text-sm focus:border-yellow-500 focus:ring-yellow-500"
                    placeholder="Min"
                  />
                  <span className="text-gray-500">to</span>
                  <input
                    type="number"
                    value={pendingTempRange.max}
                    onChange={(e) => setPendingTempRange(prev => ({ ...prev, max: e.target.value }))}
                    className="w-20 rounded-md border-gray-300 text-sm focus:border-yellow-500 focus:ring-yellow-500"
                    placeholder="Max"
                  />
                  <button
                    onClick={() => updateRange(setTempRange, setPendingTempRange, pendingTempRange, 'Temperature')}
                    className="px-2 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700"
                  >
                    Update
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Statistical Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Measurements"
          value={filteredMeasurements.length}
          status="neutral"
          description="Data points analyzed"
        />
        <StatCard
          title="Compaction Pass Rate"
          value={statistics.compaction?.passRate || 0}
          unit="%"
          status={getStatusFromPassRate(statistics.compaction?.passRate || 0)}
          description="Within specification limits"
        />
        <StatCard
          title="Process Capability (Cpk)"
          value={statistics.density?.cpk || 0}
          status={getStatusFromCpk(statistics.density?.cpk || 0)}
          description="Density process capability"
          infoTooltip="Process Capability (Cpk) measures how well the process fits within specification limits. Cpk ≥ 1.33 is considered good, 1.0-1.33 is acceptable, and < 1.0 indicates the process may produce defects. Higher values mean better process control and consistency."
        />
        <StatCard
          title="Density-Temperature Correlation"
          value={correlation}
          status={Math.abs(correlation) > 0.7 ? 'warning' : 'good'}
          description="Pearson correlation coefficient"
          infoTooltip="Correlation measures the linear relationship between density and temperature. Values close to +1 or -1 indicate strong relationships, while values near 0 indicate weak relationships. Strong correlations (|r| > 0.7) may indicate temperature-dependent quality issues."
        />
      </div>

      {/* Detailed Statistics Table */}
      <div className="bg-white rounded-lg border-2 border-gray-200">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Statistical Summary</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Metric</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Count</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mean</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Std Dev</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Min</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Max</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pass Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cpk</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Density ({detectUnits.density})</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{statistics.density?.count || 0}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatNumber(statistics.density?.mean, detectUnits.density)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatNumber(statistics.density?.stdDev, detectUnits.density)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatNumber(statistics.density?.min, detectUnits.density)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatNumber(statistics.density?.max, detectUnits.density)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{statistics.density?.passRate?.toFixed(1) || 'N/A'}%</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{statistics.density?.cpk?.toFixed(2) || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Compaction (%)</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{statistics.compaction?.count || 0}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatNumber(statistics.compaction?.mean, '%')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatNumber(statistics.compaction?.stdDev, '%')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatNumber(statistics.compaction?.min, '%')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatNumber(statistics.compaction?.max, '%')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{statistics.compaction?.passRate?.toFixed(1) || 'N/A'}%</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{statistics.compaction?.cpk?.toFixed(2) || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Voids (%)</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{statistics.voids?.count || 0}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatNumber(statistics.voids?.mean, '%')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatNumber(statistics.voids?.stdDev, '%')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatNumber(statistics.voids?.min, '%')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatNumber(statistics.voids?.max, '%')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{statistics.voids?.passRate?.toFixed(1) || 'N/A'}%</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{statistics.voids?.cpk?.toFixed(2) || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Surface Temperature ({detectUnits.temperature})</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{statistics.temperature?.count || 0}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatNumber(statistics.temperature?.mean, detectUnits.temperature)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatNumber(statistics.temperature?.stdDev, detectUnits.temperature)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatNumber(statistics.temperature?.min, detectUnits.temperature)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatNumber(statistics.temperature?.max, detectUnits.temperature)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{statistics.temperature?.passRate?.toFixed(1) || 'N/A'}%</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{statistics.temperature?.cpk?.toFixed(2) || 'N/A'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Analysis Type Selection */}
      <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Analysis Type:</label>
            <div className="flex gap-2">
              {[
                { key: 'control', label: 'Control Charts' },
                { key: 'distribution', label: 'Distribution' },
                { key: 'correlation', label: 'Correlation' },
                { key: 'trends', label: 'Time Trends' }
              ].map(type => (
                <button
                  key={type.key}
                  onClick={() => setAnalysisType(type.key)}
                  className={classNames(
                    analysisType === type.key
                      ? 'bg-yellow-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300',
                    'px-3 py-2 rounded-md text-sm font-medium transition-colors'
                  )}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Metric:</label>
            <select
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              className="rounded-md border-gray-300 text-sm focus:border-yellow-500 focus:ring-yellow-500"
            >
              <option value="density">Density</option>
              <option value="compaction">Compaction</option>
              <option value="voids">Voids</option>
              <option value="temperature">Surface Temperature</option>
            </select>
          </div>
        </div>
      </div>

      {/* Analysis Charts */}
      <div className="space-y-8">
        {analysisType === 'control' && (
          <div className="relative">
            <ControlChart
              data={filteredMeasurements}
              dataKey={currentMetricProps.dataKey}
              title={selectedMetric.charAt(0).toUpperCase() + selectedMetric.slice(1)}
              unit={currentMetricProps.unit}
              referenceRange={currentMetricProps.range}
              onDateRangeChange={({ dateRange: newDateRange, startDate, endDate }) => {
                setDateRange(newDateRange);
                setCustomStartDate(startDate);
                setCustomEndDate(endDate);
                setIsFiltered(true);
                
                const [year, month, day] = startDate.split('-').map(Number);
                const filterDate = new Date(year, month - 1, day);
                const dateString = filterDate.toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                });
                setDateRangeNotification(`Filtered to ${dateString}`);
                
                setTimeout(() => {
                  setDateRangeNotification('');
                }, 3000);
              }}
            />
            
            {/* Reset Button */}
            {isFiltered && (
              <button
                onClick={resetDateFilter}
                className="absolute top-4 right-4 inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 z-10"
              >
                <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Reset Filter
              </button>
            )}
          </div>
        )}

        {analysisType === 'distribution' && (
          <DistributionChart
            data={filteredMeasurements}
            dataKey={currentMetricProps.dataKey}
            title={selectedMetric.charAt(0).toUpperCase() + selectedMetric.slice(1)}
            unit={currentMetricProps.unit}
            referenceRange={currentMetricProps.range}
          />
        )}

        {analysisType === 'correlation' && (
          <CorrelationChart
            data={filteredMeasurements}
            detectUnits={detectUnits}
            correlation={correlation}
            formatNumber={formatNumber}
          />
        )}

        {analysisType === 'trends' && (
          <TrendsChart
            data={filteredMeasurements}
            selectedMetric={selectedMetric}
            currentMetricProps={currentMetricProps}
            statistics={statistics}
            formatNumber={formatNumber}
          />
        )}
      </div>

      {/* Quality Alerts Section */}
      {statistics.density && (
        <div className="bg-white rounded-lg border-2 border-gray-200">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quality Control Alerts</h3>
            <div className="space-y-3">
              {statistics.density.passRate < 90 && (
                <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-red-800">
                      Low Pass Rate: Density pass rate is {statistics.density.passRate.toFixed(1)}% (below 90% threshold)
                    </p>
                  </div>
                </div>
              )}
              
              {statistics.density.cpk < 1.0 && (
                <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-red-800">
                      Poor Process Capability: Cpk is {statistics.density.cpk.toFixed(2)} (below 1.0 minimum)
                    </p>
                  </div>
                </div>
              )}
              
              {Math.abs(correlation) > 0.7 && (
                <div className="flex items-center p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-yellow-800">
                      Strong Temperature Correlation: Density shows strong correlation with temperature ({correlation.toFixed(3)})
                    </p>
                  </div>
                </div>
              )}
              
              {statistics.density.passRate >= 95 && statistics.density.cpk >= 1.33 && Math.abs(correlation) <= 0.3 && (
                <div className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-green-800">
                      Excellent Quality Control: All metrics within acceptable ranges
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Export Options */}
      <div className="bg-white rounded-lg border-2 border-gray-200">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Export Analysis</h3>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => {
                // Export statistical summary as CSV
                const data = [
                  ['Metric', 'Count', 'Mean', 'Std Dev', 'Min', 'Max', 'Pass Rate %', 'Cpk'],
                  [`Density (${detectUnits.density})`, statistics.density?.count || 0, formatNumber(statistics.density?.mean, detectUnits.density), 
                   formatNumber(statistics.density?.stdDev, detectUnits.density), formatNumber(statistics.density?.min, detectUnits.density),
                   formatNumber(statistics.density?.max, detectUnits.density), statistics.density?.passRate?.toFixed(1) || 'N/A',
                   statistics.density?.cpk?.toFixed(2) || 'N/A'],
                  ['Compaction (%)', statistics.compaction?.count || 0, formatNumber(statistics.compaction?.mean, '%'),
                   formatNumber(statistics.compaction?.stdDev, '%'), formatNumber(statistics.compaction?.min, '%'),
                   formatNumber(statistics.compaction?.max, '%'), statistics.compaction?.passRate?.toFixed(1) || 'N/A',
                   statistics.compaction?.cpk?.toFixed(2) || 'N/A'],
                  ['Voids (%)', statistics.voids?.count || 0, formatNumber(statistics.voids?.mean, '%'),
                   formatNumber(statistics.voids?.stdDev, '%'), formatNumber(statistics.voids?.min, '%'),
                   formatNumber(statistics.voids?.max, '%'), statistics.voids?.passRate?.toFixed(1) || 'N/A',
                   statistics.voids?.cpk?.toFixed(2) || 'N/A'],
                  [`Temperature (${detectUnits.temperature})`, statistics.temperature?.count || 0, formatNumber(statistics.temperature?.mean, detectUnits.temperature),
                   formatNumber(statistics.temperature?.stdDev, detectUnits.temperature), formatNumber(statistics.temperature?.min, detectUnits.temperature),
                   formatNumber(statistics.temperature?.max, detectUnits.temperature), statistics.temperature?.passRate?.toFixed(1) || 'N/A',
                   statistics.temperature?.cpk?.toFixed(2) || 'N/A']
                ];
                
                const csvContent = data.map(row => row.join(',')).join('\n');
                const blob = new Blob([csvContent], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'statistical_summary.csv';
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
            >
              <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export Statistics
            </button>
            
            <button
              onClick={() => {
                const csvContent = [
                  ['Timestamp', 'Density', 'Compaction', 'Voids', 'Surface Temperature'],
                  ...filteredMeasurements.map(m => [
                    new Date(m.LocalTimeOfMeasurement).toISOString(),
                    m.Density || '',
                    m.Compaction || '',
                    m.Voids || '',
                    m.SurfaceTemp || ''
                  ])
                ].map(row => row.join(',')).join('\n');
                
                const blob = new Blob([csvContent], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'filtered_measurements.csv';
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
            >
              <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
              </svg>
              Export Data
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}